import { blurHashToDataURL } from '@/frontend/utils/blurhash-to-dataurl'
import { MediaGroupType } from '@/payload-custom-types'
import { Media as MediaType } from '@/payload-types'
import clsx from 'clsx'
import Image, { getImageProps } from 'next/image'

interface MediaElementProps {
  mediaGroup: MediaGroupType['mediaGroup']
  className?: string
  type?: string
  showDescription?: boolean | null | undefined
  quality?: number
  sizeLayout?: string
  priority?: boolean
  ref?: React.RefObject<HTMLDivElement | null>
  id?: string
  videoAttributes?: {
    autoPlay?: boolean
    loop?: boolean
    controls?: boolean
    muted?: boolean
    preload?: true | 'auto' | 'metadata' | 'none' | string
  }
  artDirection?: boolean
  lazy?: boolean
}

const Media = ({ mediaGroup, className, type, showDescription = false, ref, ...props }: MediaElementProps) => {
  if (!mediaGroup) return null
  if (typeof mediaGroup?.media === 'string') return null

  const caption = mediaGroup?.media?.caption ? mediaGroup?.media?.caption : null

  let assetRender
  let videoClass = null

  switch (type) {
    case 'image':
      assetRender = <ImageAsset image={mediaGroup?.media} {...props} />
      break
    case 'video':
      assetRender = <VideoAsset video={mediaGroup?.media} videoAttributes={props.videoAttributes} />
      videoClass = 'video'
      break
    default:
      assetRender = null
  }

  return (
    <div id={props.id} ref={ref} className={clsx('media', videoClass, className)}>
      {assetRender}
      {showDescription ? <>{caption && <span className="media-caption">{caption}</span>}</> : null}
    </div>
  )
}
Media.displayName = 'Media'

interface ImageTypes {
  image: MediaType
  quality?: number
  priority?: boolean
  sizeLayout?: string
  artDirection?: boolean
}

const ImageAsset = ({ image, ...props }: ImageTypes) => {
  if (!image) return null
  if (!image.url) return null
  if (!image.width) return null
  if (!image.height) return null

  type ImageProps = {
    url: string
    width: number
    height: number
    quality?: number
    priority?: boolean
    alt?: string
    blurhash?: string | null | undefined
    lazy?: boolean
  }

  const additionalImageProps = {
    quality: props.quality || 100,
    priority: props.priority || false,
    blurhash: image.blurhash,
  }

  let imageProps: ImageProps = {
    url: image.url || '',
    width: image.width || 0,
    height: image.height || 0,
    alt: image.alt || '',
    ...additionalImageProps,
  }

  if (props.sizeLayout) {
    switch (props.sizeLayout) {
      case 'thumbnail':
        imageProps = {
          width: image.sizes?.thumbnail?.width || 400,
          height: image.sizes?.thumbnail?.height || 300,
          url: image.sizes?.thumbnail?.url || image.url,
          ...additionalImageProps,
        }
        break
      case 'card':
        imageProps = {
          width: image.sizes?.card?.width || 768,
          height: image.sizes?.card?.height || 1024,
          url: image.sizes?.card?.url || image.url,
          ...additionalImageProps,
        }
        break
      case 'card-big':
        imageProps = {
          width: image.sizes?.cardBig?.width || 1200,
          height: image.sizes?.cardBig?.height || 1800,
          url: image.sizes?.cardBig?.url || image.url,
          ...additionalImageProps,
        }
        break
      case 'square':
        imageProps = {
          width: image.sizes?.square?.width || 1024,
          height: image.sizes?.square?.height || 1024,
          url: image.sizes?.square?.url || image.url,
          ...additionalImageProps,
        }
        break
      case 'square-big':
        imageProps = {
          width: image.sizes?.squareBig?.width || 1024,
          height: image.sizes?.squareBig?.height || 1024,
          url: image.sizes?.squareBig?.url || image.url,
          ...additionalImageProps,
        }
        break
      case 'tablet':
        imageProps = {
          width: image.sizes?.tablet?.width || 1024,
          height: image.sizes?.tablet?.height || 1024,
          url: image.sizes?.tablet?.url || image.url,
          ...additionalImageProps,
        }
        break
      case 'mobile':
        imageProps = {
          width: image.sizes?.mobile?.width || 768,
          height: image.sizes?.mobile?.height || 768,
          url: image.sizes?.mobile?.url || image.url,
          ...additionalImageProps,
        }
      default:
        break
    }
  }

  // check if image is url is gif
  const isGif = imageProps.url.endsWith('.gif')

  // if artDirection is true, use picture element instead of next/image
  if (props.artDirection) {
    const common = {
      alt: imageProps.alt || '',
      quality: imageProps.quality,
      priority: imageProps.priority,
    }

    const {
      props: { srcSet: desktop },
    } = getImageProps({
      ...common,
      width: (image.sizes?.background?.width as number) || imageProps.width,
      height: (image.sizes?.background?.height as number) || imageProps.height,
      src: (image.sizes?.background?.url as string) || imageProps.url,
    })

    const {
      props: { srcSet: mobile, ...rest },
    } = getImageProps({
      ...common,
      width: (image.sizes?.square?.width as number) || imageProps.width,
      height: (image.sizes?.square?.height as number) || imageProps.height,
      src: (image.sizes?.square?.url as string) || imageProps.url,
    })

    return (
      <picture>
        <source media="(min-width: 768px)" srcSet={desktop} />
        <source media="(min-width: 320px)" srcSet={mobile} />
        <img {...rest} alt={imageProps.alt || ''} />
      </picture>
    )
  }

  return (
    <Image
      src={imageProps.url}
      alt={imageProps.alt || ''}
      width={imageProps.width}
      height={imageProps.height}
      {...(imageProps.blurhash && { placeholder: 'blur' })}
      {...(imageProps.blurhash && {
        blurDataURL: blurHashToDataURL(imageProps.blurhash, 8, 8),
      })}
      quality={imageProps.quality}
      {...(imageProps.priority && { priority: true })}
      {...(imageProps.lazy && { loading: 'lazy' })}
      {...(isGif && { unoptimized: true })}
      sizes="(max-width: 768px) 100vw, (max-width: 1023px) 100vw, (min-width: 1024px) 100vw"
    />
  )
}
ImageAsset.displayName = 'ImageAsset'

interface VideoTypes {
  video: MediaType
  videoAttributes?: {
    autoPlay?: boolean
    loop?: boolean
    controls?: boolean
    muted?: boolean
    preload?: true | 'auto' | 'metadata' | 'none' | string
  }
}

const VideoAsset = ({
  video,
  videoAttributes = {
    controls: true,
    autoPlay: false,
    loop: false,
    muted: false,
    preload: 'auto',
  },
}: VideoTypes) => {
  if (!video) return null
  if (!video.url) return null
  if (!video.mimeType) return null
  const { controls, autoPlay, loop, muted, preload } = videoAttributes

  return (
    <video {...(controls && { controls })} {...(preload && { preload: `${preload}` })} {...(autoPlay && { autoPlay })} {...(loop && { loop })} {...(muted && { muted })} playsInline={true}>
      <source src={video.url} type={video.mimeType} />
    </video>
  )
}
VideoAsset.displayName = 'VideoAsset'

const getMediaMimeType = (media: MediaType) => {
  if (media) {
    const mimeType = media.mimeType
    if (mimeType === 'video/mp4' || mimeType === 'video/webm' || mimeType === 'video/ogg' || mimeType === 'video/mov') {
      return 'video'
    }
    if (mimeType === 'image/jpeg' || mimeType === 'image/png' || mimeType === 'image/jpg' || mimeType === 'image/svg+xml' || mimeType === 'image/gif' || mimeType === 'image/webp') {
      return 'image'
    }
  }
}
getMediaMimeType.displayName = 'getMediaMimeType'

export { Media, ImageAsset, VideoAsset, getMediaMimeType }
